import { CoreLink } from '@uncinc/react-drupal-core';
import type {
  LinkField,
  ParagraphEntity, ReferenceField,
} from '@uncinc/react-drupal-core/types';
import { List } from 'immutable';
import React from 'react';
import { Button, Container } from '@uncinc/uncinc-react-kitchen-sink';
import { useTranslation } from 'react-i18next';
import type { NodePartner } from '../../../../types/uncinc_profile/nodes';
import type { TaxonomyGeneric, TaxonomyTheme } from '../../../../types/uncinc_profile/taxonomy';
import { ParagraphsRenderer } from '../../../../components';

interface IntroductionProps {
  paragraphs: List<ReferenceField<ParagraphEntity>>
  sidebarProps: SidebarProps;
}

interface SidebarProps {
  field_partner: [ReferenceField<NodePartner>];
  field_themes: ReferenceField<TaxonomyTheme>[];
  field_type: ReferenceField<TaxonomyGeneric>[];
  field_expertises: ReferenceField<TaxonomyGeneric>[];
  field_technologies: ReferenceField<TaxonomyGeneric>[];
  field_link: LinkField[];
}

interface SidebarItemLink {
  name: string;
  path?: string;
}

interface SidebarItemProps {
  title: string;
  links?: SidebarItemLink[];
}

const SidebarItem: React.FC<SidebarItemProps> = ({ title, links }) => (
  <div className="IntroductionSidebar__item">
    <div className="IntroductionSidebar__title">
      {title}
    </div>
    {links && links.length > 0 && (
      <div className="IntroductionSidebar__content">
        {links.map(({ name, path }) => {
          if (!path) {
            return (
              <span>
                {name}
              </span>
            );
          }

          return (
            <CoreLink key={path} href={path}>
              {name}
            </CoreLink>
          );
        })}
      </div>
    )}
  </div>
);

const SidebarComponent: React.FC<SidebarProps>
= ({
  field_partner,
  field_themes,
  field_type,
  field_expertises,
  field_technologies,
  field_link,
}) => {
  const { t } = useTranslation();

  return (
    <div className="IntroductionSidebar__wrapper">
      { field_partner?.length > 0 && (
      <SidebarItem
        title={t('content_types.project.sidebar.field_partner')}
        links={field_partner.map((partner) => ({
          name: partner.entity.title[0].value,
        }))}
      />
      )}
      { field_type?.length > 0 && (
      <SidebarItem
        title={t('content_types.project.sidebar.field_type')}
        links={field_type.map((fieldType) => ({
          name: fieldType.entity.name[0].value,
          path: fieldType.entity.paths.nl,
        }))}
      />
      )}
      { field_themes?.length > 0 && (
      <SidebarItem
        title={t('content_types.project.sidebar.field_themes')}
        links={field_themes.map((fieldTheme) => ({
          name: fieldTheme.entity.name[0].value,
          path: fieldTheme.entity.paths.nl,
        }))}
      />
      )}
      { field_technologies?.length > 0 && (
      <SidebarItem
        title={t('content_types.project.sidebar.field_technologies')}
        links={field_technologies.map((technology) => ({
          name: technology.entity.name[0].value,
          path: technology.entity.paths.nl,
        }))}
      />
      )}
      { field_expertises?.length > 0 && (
      <SidebarItem
        title={t('content_types.project.sidebar.field_expertises')}
        links={field_expertises.map((expertise) => ({
          name: expertise.entity.name[0].value,
          path: expertise.entity.paths.nl,
        }))}
      />
      )}
      { field_link?.length > 0 && (
      <Button href={field_link[0]?.uri}>
        {field_link[0]?.title}
      </Button>
      )}
    </div>
  );
};

const IntroductionComponent: React.FC<IntroductionProps>
= ({ paragraphs, sidebarProps }) => {
  const hasProjectIntroduction = (paragraphs?.size ?? 0) > 0;

  const classes = hasProjectIntroduction ? 'col-md-12 col-lg-3 offset-lg-1' : 'col-12 no-paragraphs';

  return (
    <Container>
      <div className="row">
        <div className={hasProjectIntroduction ? 'col-md-12 col-lg-8' : ''}>
          { hasProjectIntroduction && (
            <div className="IntroductionWrapper">
              <ParagraphsRenderer
                paragraphs={paragraphs as any}
                noBootstrap
              />
            </div>
          )}
        </div>
        <div className={classes}>
          <SidebarComponent
            {...sidebarProps}
          />
        </div>
      </div>
    </Container>
  );
};

export default IntroductionComponent;
